import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { ArrowBack, ImageOutlined, Refresh, Send } from '@mui/icons-material';
import { SUPPORT_CATEGORIES } from '../../config/support';
import { SupportService } from '../../services/supportService';
import type { SupportMessage, SupportTicket, SupportTicketReceipt } from '../../types';
import { TicketStatus, displayDate } from './supportTicketPresentation';

function Screenshot({ ticketId, index, filename }: { ticketId: string; index: number; filename: string }) {
  const [image, setImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const active = useRef(true);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const open = async () => {
    setLoading(true); setError(false);
    try { const value = await SupportService.getScreenshot(ticketId, index); if (active.current) setImage(value); }
    catch { if (active.current) setError(true); }
    finally { if (active.current) setLoading(false); }
  };
  return <Box>
    {image ? <>
      <Box component="img" src={image} alt={filename} sx={{ display: 'block', maxWidth: '100%', maxHeight: 420, borderRadius: 1 }} />
      <Button size="small" onClick={() => setImage(null)}>Hide {filename}</Button>
    </> : <Button size="small" startIcon={loading ? <CircularProgress size={16} /> : <ImageOutlined />} disabled={loading} onClick={open}>
      {error ? 'Retry' : 'View'} {filename}
    </Button>}
    {error && <Typography role="alert" variant="body2" color="error">Could not load this screenshot.</Typography>}
  </Box>;
}

export default function SupportTicketConversation({ id, onBack, admin = false }: {
  id: string; onBack: () => void; admin?: boolean;
}) {
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const failedCursor = useRef<string | undefined>();
  const [actionError, setActionError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const sequence = useRef(0);
  const active = useRef(true);
  const busy = useRef(false);
  const pendingReply = useRef<{ body: string; requestId: string } | null>(null);
  const pendingStatus = useRef<{ status: SupportTicketReceipt['status']; requestId: string } | null>(null);

  const load = useCallback(async (older?: string) => {
    const request = ++sequence.current;
    setLoading(true); setLoadError(null); failedCursor.current = older;
    try {
      const [detail, page] = await Promise.all([
        older ? Promise.resolve(null) : SupportService.getTicket(id), SupportService.listMessages(id, older),
      ]);
      if (!active.current || request !== sequence.current) return;
      if (detail) setTicket(detail);
      setMessages(previous => older ? Array.from(new Map([...page.messages, ...previous].map(message => [message.id, message])).values()) : page.messages);
      setCursor(page.nextCursor);
      // Only acknowledge messages actually fetched. A concurrent reply stays unread.
      if (!older) void SupportService.markRead(id, page.messages[page.messages.length - 1]?.sequence || 0, admin).catch(() => {});
    } catch { if (active.current && request === sequence.current) setLoadError(older ? 'Could not load older replies. Please try again.' : 'We could not load this ticket. Please try again.'); }
    finally { if (active.current && request === sequence.current) setLoading(false); }
  }, [id, admin]);
  useEffect(() => {
    active.current = true; void load();
    const invalidateRequests = () => { active.current = false; sequence.current++; };
    return invalidateRequests;
  }, [load]);

  const reply = async () => {
    const body = draft.trim();
    if (!body || busy.current) return;
    busy.current = true; setSaving(true); setActionError(null); setNotice(null);
    if (pendingReply.current?.body !== body) pendingReply.current = { body, requestId: crypto.randomUUID() };
    try {
      const result = await SupportService.reply(id, body, pendingReply.current.requestId, admin);
      if (!active.current) return;
      setTicket(previous => previous ? { ...previous, ...result.ticket } : previous);
      setMessages(previous => previous.some(message => message.id === result.message.id) ? previous : [...previous, result.message]);
      setDraft(''); pendingReply.current = null;
      setNotice(result.message.notificationStatus === 'accepted' ? 'Reply saved.' : 'Reply saved. The email notification has not been confirmed.');
      await load();
    } catch { if (active.current) setActionError('We could not confirm your reply was saved. Your text is still here; try sending it again.'); }
    finally { busy.current = false; if (active.current) setSaving(false); }
  };
  const changeStatus = async (status: SupportTicketReceipt['status']) => {
    if (busy.current) return;
    busy.current = true; setSaving(true); setActionError(null);
    if (pendingStatus.current?.status !== status) pendingStatus.current = { status, requestId: crypto.randomUUID() };
    try {
      const updated = await SupportService.setStatus(id, status, pendingStatus.current.requestId);
      if (active.current) { setTicket(previous => previous ? { ...previous, ...updated } : previous); pendingStatus.current = null; }
    } catch { if (active.current) setActionError('Could not update the status. Please try again.'); }
    finally { busy.current = false; if (active.current) setSaving(false); }
  };
  const fields = ticket ? [
    ['Study', ticket.details.studyName || ticket.details.studyId], ['Participant ID', ticket.details.participantIdentifier],
    ['Where it stopped', ticket.details.enrollmentStep], ['Participants affected', ticket.details.participantsAffected],
    ['Error message', ticket.details.errorMessage], ['Browser or device', ticket.details.browserDevice || ticket.details.browserInfo],
    ['Additional notes', ticket.details.additionalNotes],
  ].filter(([, value]) => value !== undefined && value !== '') : [];

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <Button size="small" startIcon={<ArrowBack />} disabled={saving} onClick={onBack}>{admin ? 'Back to inbox' : 'Back to my tickets'}</Button>
      <Button size="small" startIcon={<Refresh />} disabled={loading || saving} onClick={() => load()}>Refresh ticket</Button>
    </Box>
    {loadError && <Alert severity="error" action={<Button color="inherit" onClick={() => load(failedCursor.current)}>Retry</Button>}>{loadError}</Alert>}
    {loading && !ticket && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} aria-label="Loading ticket" /></Box>}
    {ticket && <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>{SUPPORT_CATEGORIES[ticket.details.category]?.label || 'Support ticket'}</Typography>
        <TicketStatus status={ticket.status} />
      </Box>
      <Typography variant="body2" sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere', userSelect: 'all' }}>{ticket.id}</Typography>
      {admin && <Typography variant="body2">{ticket.requesterName} · {ticket.requesterEmail}</Typography>}
      <Typography variant="caption" color="text.secondary">Submitted {displayDate(ticket.createdAt)}</Typography>
      {ticket.details.urgency && <Typography variant="body2" color="warning.main">Marked as blocking study work</Typography>}
      {ticket.notificationStatus !== 'accepted' && <Alert severity="warning">This ticket is saved. The original email notification to our team has not been confirmed.</Alert>}
      {admin && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {ticket.status !== 'in_progress' && <Button size="small" variant="outlined" disabled={saving} onClick={() => changeStatus('in_progress')}>Mark in progress</Button>}
        <Button size="small" variant="outlined" disabled={saving} onClick={() => changeStatus(ticket.status === 'resolved' ? 'open' : 'resolved')}>
          {ticket.status === 'resolved' ? 'Reopen ticket' : 'Resolve ticket'}
        </Button>
      </Box>}
      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{admin ? 'Original request' : 'Your message'}</Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{ticket.details.description}</Typography>
      </Box>
      {fields.length > 0 && <Box component="details"><Typography component="summary" sx={{ cursor: 'pointer' }}>Request details</Typography>
        {fields.map(([label, value]) => <Box key={label} sx={{ mt: 1 }}><Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{value}</Typography></Box>)}
      </Box>}
      {ticket.attachments.map((attachment, index) => <Screenshot key={index} ticketId={ticket.id} index={index} filename={attachment.filename} />)}
      <Typography variant="subtitle2">Conversation</Typography>
      {cursor && <Button disabled={loading || saving} onClick={() => load(cursor)}>Load older replies</Button>}
      {!messages.length && <Typography variant="body2" color="text.secondary">No replies yet. You can add more details below.</Typography>}
      {messages.map(message => <Box key={message.id} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: message.authorRole === 'support' ? 'action.hover' : 'background.paper' }}>
        <Typography variant="subtitle2">{message.authorName}</Typography>
        <Typography variant="caption" color="text.secondary">{displayDate(message.createdAt)}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.body}</Typography>
        {message.notificationStatus && message.notificationStatus !== 'accepted' && <Typography variant="caption" color="text.secondary">Reply saved; email notification not confirmed.</Typography>}
      </Box>)}
      {notice && <Alert severity="info">{notice}</Alert>}
      {actionError && <Alert severity="error">{actionError}</Alert>}
      <TextField label="Your reply" multiline minRows={3} value={draft} disabled={saving} fullWidth
        inputProps={{ maxLength: 5000 }} onChange={event => setDraft(event.target.value)} />
      <Typography variant="caption" color="text.secondary">Replies stay in this ticket. We also send an email notification.{!admin && ticket.status === 'resolved' ? ' Sending a reply will reopen it.' : ''}</Typography>
      <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Send />} disabled={saving || loading || !draft.trim()} onClick={reply} sx={{ alignSelf: 'flex-end' }}>Send reply</Button>
    </>}
  </Box>;
}
